/**
 * Four-State Visual Feedback System
 * Progressive enhancement with Empty/Ready/Active/Success state architecture
 */

import {
  VisualFeedbackState,
  VisualFeedbackConfig,
  EmptyStateConfig,
  ReadyStateConfig,
  ActiveStateConfig,
  SuccessStateConfig,
  VisualFeedbackContext,
  VisualState,
  TransitionConfig,
} from "./types";
import { HardwareAcceleratedAnimationFramework } from "./HardwareAcceleratedAnimationFramework";
import { Position, Rectangle, ComponentType } from "@/types";

/**
 * Main Four-State Visual Feedback System
 * Orchestrates all visual feedback interactions with progressive enhancement
 */
export class FourStateVisualFeedbackSystem {
  private animationFramework: HardwareAcceleratedAnimationFramework;
  private currentState: VisualFeedbackState = VisualFeedbackState.EMPTY;
  private previousState: VisualFeedbackState | null = null;
  private activeElements: Map<string, HTMLElement> = new Map();
  private stateConfigurations: StateConfigurationManager;
  private transitionManager: StateTransitionManager;
  private contextTracker: ContextTracker;

  constructor(
    animationFramework?: HardwareAcceleratedAnimationFramework,
    initialConfig?: Partial<VisualFeedbackConfig>,
  ) {
    this.animationFramework =
      animationFramework || new HardwareAcceleratedAnimationFramework();
    this.stateConfigurations = new StateConfigurationManager();
    this.transitionManager = new StateTransitionManager(
      this.animationFramework,
    );
    this.contextTracker = new ContextTracker();

    this.initializeSystem();
  }

  /**
   * Initialize the four-state system
   */
  private initializeSystem(): void {
    this.setupDefaultConfigurations();
    this.initializeStyleSheets();
    this.attachEventListeners();
  }

  /**
   * Setup default state configurations
   */
  private setupDefaultConfigurations(): void {
    // Empty/Ready State Configuration
    this.stateConfigurations.setEmptyStateConfig({
      gridPattern: {
        enabled: true,
        size: 20,
        opacity: 0.1,
        color: "hsl(var(--muted))",
        animationCycle: 3,
      },
      helpPrompts: {
        enabled: true,
        text: "Drag components here to start building",
        position: "center",
        fadeInDelay: 500,
      },
      paletteHighlight: {
        enabled: true,
        glowIntensity: 0.3,
        pulseCycle: 2,
      },
    });

    // Ready/Hover State Configuration
    this.stateConfigurations.setReadyStateConfig({
      dropZone: {
        borderAnimation: "flow",
        borderColor: "hsl(var(--primary))",
        backgroundOpacity: 0.05,
        animationSpeed: 2,
      },
      ghostPreview: {
        enabled: true,
        opacity: 0.8,
        shadowIntensity: 0.25,
        snapIndicators: true,
      },
      alignmentGuides: {
        enabled: true,
        color: "hsl(var(--primary))",
        thickness: 1,
        magneticThreshold: 16,
      },
      invalidZones: {
        enabled: true,
        overlayColor: "hsl(var(--destructive))",
        overlayOpacity: 0.1,
        prohibitionIcon: true,
      },
    });

    // Active/Dragging State Configuration
    this.stateConfigurations.setActiveStateConfig({
      componentTransform: {
        scale: 1.05,
        rotation: 1,
        elevation: 50,
        shadowBlur: 20,
        shadowOpacity: 0.4,
        filterBrightness: 1.1,
      },
      originMarker: {
        enabled: true,
        size: 16,
        color: "hsl(var(--primary))",
        animationType: "ping",
      },
      realTimeGuides: {
        enabled: true,
        snapThreshold: 16,
        color: "hsl(var(--primary))",
        opacity: 0.6,
        animationSpeed: 0.2,
      },
      autoScroll: {
        enabled: true,
        edgeThreshold: 50,
        scrollSpeed: "adaptive",
        maxSpeed: 40,
      },
    });

    // Success/Placement State Configuration
    this.stateConfigurations.setSuccessStateConfig({
      placementAnimation: {
        duration: 600,
        easing: "cubic-bezier(0.34, 1.56, 0.64, 1)",
        scaleSequence: [1.1, 1.05, 1],
        opacitySequence: [0.8, 0.9, 1],
        shadowSequence: [
          "0 0 20px rgba(var(--success-rgb), 0.6)",
          "0 2px 8px rgba(var(--primary-rgb), 0.2)",
          "0 2px 8px rgba(var(--primary-rgb), 0.2)",
        ],
      },
      successIndicator: {
        enabled: true,
        icon: "✓",
        size: 24,
        color: "hsl(var(--success))",
        position: "top-right",
        animationType: "bounce",
        displayDuration: 1500,
      },
      settlingEffect: {
        enabled: true,
        finalShadow: "0 2px 8px rgba(var(--primary-rgb), 0.2)",
        settleSpeed: 400,
      },
      propertyPanelActivation: {
        enabled: true,
        activationDelay: 300,
        transitionType: "slide",
      },
    });
  }

  /**
   * Initialize CSS styles for visual feedback
   */
  private initializeStyleSheets(): void {
    const styleSheet = this.createStyleSheet();
    document.head.appendChild(styleSheet);
  }

  /**
   * Create CSS stylesheet for visual feedback
   */
  private createStyleSheet(): HTMLStyleElement {
    const style = document.createElement("style");
    style.id = "terra-visual-feedback-styles";

    style.textContent = `
      /* Empty/Ready State Styles */
      .terra-canvas-empty {
        background: repeating-linear-gradient(
          45deg,
          transparent,
          transparent 19px,
          rgba(var(--muted-rgb), 0.1) 19px,
          rgba(var(--muted-rgb), 0.1) 21px
        );
        transition: background 0.3s ease;
      }

      .terra-empty-prompt {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        color: hsl(var(--muted-foreground));
        font-size: 1.125rem;
        font-weight: 500;
        border: 2px dashed hsl(var(--muted));
        border-radius: 0.5rem;
        padding: 3rem;
        text-align: center;
        opacity: 0;
        animation: gentle-pulse 3s ease-in-out infinite;
      }

      @keyframes gentle-pulse {
        0%, 100% { opacity: 0.6; }
        50% { opacity: 0.8; }
      }

      .terra-palette-highlight {
        box-shadow: 0 0 20px rgba(var(--primary-rgb), 0.3);
        animation: palette-glow 2s ease-in-out infinite alternate;
      }

      @keyframes palette-glow {
        from { box-shadow: 0 0 20px rgba(var(--primary-rgb), 0.2); }
        to { box-shadow: 0 0 30px rgba(var(--primary-rgb), 0.4); }
      }

      /* Ready/Hover State Styles */
      .terra-drop-zone-ready {
        border: 2px solid hsl(var(--primary) / 0.5);
        background: hsl(var(--primary) / 0.05);
        transition: all 0.2s ease-out;
        position: relative;
        overflow: hidden;
      }

      .terra-drop-zone-ready::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba(var(--primary-rgb), 0.1) 50%,
          transparent 100%
        );
        animation: border-flow 2s linear infinite;
      }

      @keyframes border-flow {
        0% { left: -100%; }
        100% { left: 100%; }
      }

      .terra-ghost-preview {
        opacity: 0.8;
        border: 2px solid hsl(var(--primary));
        box-shadow: 0 8px 25px rgba(var(--primary-rgb), 0.25);
        filter: drop-shadow(0 0 10px rgba(var(--primary-rgb), 0.3));
        pointer-events: none;
        z-index: 1000;
      }

      .terra-snap-guide {
        position: absolute;
        background: hsl(var(--primary));
        opacity: 0.6;
        z-index: 999;
        animation: guide-pulse 0.3s ease-out;
      }

      .terra-snap-guide-horizontal {
        height: 1px;
        width: 100%;
      }

      .terra-snap-guide-vertical {
        width: 1px;
        height: 100%;
      }

      @keyframes guide-pulse {
        0% { opacity: 0; }
        100% { opacity: 0.6; }
      }

      .terra-invalid-zone {
        background: hsl(var(--destructive) / 0.1);
        border: 2px solid hsl(var(--destructive) / 0.5);
        cursor: not-allowed;
        animation: invalid-shake 0.3s ease-in-out;
      }

      @keyframes invalid-shake {
        0%, 100% { transform: translateX(0); }
        25% { transform: translateX(-4px); }
        75% { transform: translateX(4px); }
      }

      /* Active/Dragging State Styles */
      .terra-component-dragging {
        transform: scale(1.05) rotate(1deg) translateZ(0);
        box-shadow: 0 10px 30px rgba(var(--primary-rgb), 0.4);
        z-index: 50;
        filter: brightness(1.1) drop-shadow(0 10px 20px rgba(var(--primary-rgb), 0.4));
        transition: none !important; /* Disable transitions during drag */
        will-change: transform, opacity;
        backface-visibility: hidden;
      }

      .terra-drag-origin-marker {
        position: absolute;
        width: 16px;
        height: 16px;
        background: hsl(var(--primary));
        border-radius: 50%;
        animation: origin-ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;
        z-index: 49;
      }

      @keyframes origin-ping {
        75%, 100% {
          transform: scale(2);
          opacity: 0;
        }
      }

      /* Success/Placement State Styles */
      .terra-placement-success {
        animation: placement-success 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
      }

      @keyframes placement-success {
        0% {
          transform: scale(1.1);
          opacity: 0.8;
        }
        50% {
          transform: scale(1.05);
          opacity: 0.9;
          box-shadow: 0 0 20px rgba(var(--success-rgb), 0.6);
        }
        100% {
          transform: scale(1);
          opacity: 1;
          box-shadow: 0 2px 8px rgba(var(--primary-rgb), 0.2);
        }
      }

      .terra-success-indicator {
        position: absolute;
        top: -8px;
        right: -8px;
        width: 24px;
        height: 24px;
        background: hsl(var(--success));
        color: white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.875rem;
        font-weight: bold;
        animation: success-bounce 0.5s ease-out;
        z-index: 51;
      }

      @keyframes success-bounce {
        0% {
          transform: scale(0) rotate(0deg);
          opacity: 0;
        }
        50% {
          transform: scale(1.2) rotate(180deg);
          opacity: 1;
        }
        100% {
          transform: scale(1) rotate(360deg);
          opacity: 1;
        }
      }

      /* Performance optimizations */
      .terra-hardware-accelerated {
        transform: translateZ(0);
        will-change: transform, opacity;
        backface-visibility: hidden;
      }

      /* Reduced motion support */
      @media (prefers-reduced-motion: reduce) {
        .terra-canvas-empty,
        .terra-drop-zone-ready,
        .terra-component-dragging,
        .terra-placement-success,
        .terra-success-indicator,
        .terra-drag-origin-marker,
        .terra-snap-guide,
        .terra-palette-highlight {
          animation-duration: 0.01ms !important;
          animation-iteration-count: 1 !important;
          transition-duration: 0.01ms !important;
        }
      }

      /* High contrast mode support */
      @media (prefers-contrast: high) {
        .terra-component-dragging {
          outline: 3px solid ButtonText;
          background-color: Highlight;
          color: HighlightText;
        }

        .terra-drop-zone-ready {
          border: 3px solid ActiveText;
          background: rgba(255, 255, 255, 0.1);
        }

        .terra-snap-guide {
          background-color: ButtonText;
          height: 2px;
        }
      }

      /* Agricultural Context Optimizations */
      .terra-agricultural-mode .terra-component-dragging {
        transform: scale(1.08) rotate(0deg) translateZ(0); /* Reduced rotation for clarity */
        box-shadow: 0 12px 40px rgba(var(--primary-rgb), 0.6);
        border: 3px solid hsl(var(--primary));
        filter: brightness(1.2) contrast(1.1);
      }

      .terra-agricultural-mode .terra-drop-zone-ready {
        border: 3px dashed hsl(var(--primary));
        background: hsl(var(--primary) / 0.08);
        min-height: 60px; /* Larger drop zones for gloved hands */
      }

      .terra-agricultural-mode .terra-success-indicator {
        width: 32px;
        height: 32px;
        font-size: 1.125rem;
        border: 2px solid white;
        box-shadow: 0 0 12px rgba(var(--success-rgb), 0.8);
      }

      .terra-outdoor-mode {
        filter: brightness(1.3) contrast(1.2) saturate(1.1);
      }

      .terra-outdoor-mode .terra-empty-prompt {
        background: rgba(255, 255, 255, 0.95);
        color: #000000;
        border: 3px solid #000000;
        text-shadow: 1px 1px 2px rgba(255, 255, 255, 0.8);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
      }

      .terra-outdoor-mode .terra-snap-guide {
        background: #000000;
        height: 3px;
        box-shadow: 0 0 4px rgba(0, 0, 0, 0.6);
      }

      /* Weather resistant mode for extreme conditions */
      .terra-weather-resistant .terra-component-dragging {
        background: linear-gradient(135deg, #000000 0%, #333333 100%);
        color: #FFFF00;
        border: 4px solid #FFFF00;
        text-shadow: 0 0 4px #000000;
        box-shadow: 0 0 16px rgba(255, 255, 0, 0.8);
      }

      .terra-weather-resistant .terra-drop-zone-ready {
        border: 4px dashed #FFFF00;
        background: rgba(255, 255, 0, 0.1);
        box-shadow: inset 0 0 8px rgba(255, 255, 0, 0.3);
      }

      /* Forced colors mode (Windows High Contrast) */
      @media (forced-colors: active) {
        .terra-component-dragging {
          forced-color-adjust: none;
          border: 2px solid Highlight;
          background: Canvas;
        }
      }

      /* Touch-friendly sizing for agricultural equipment interfaces */
      .terra-touch-agricultural {
        min-width: 48px;
        min-height: 48px;
        padding: 8px;
      }

      .terra-touch-agricultural .terra-component-dragging,
      .terra-touch-agricultural .terra-drop-zone-ready,
      .terra-touch-agricultural .terra-success-indicator {
        min-width: 48px;
        min-height: 48px;
      }
    `;

    return style;
  }

  /**
   * Attach system event listeners
   */
  private attachEventListeners(): void {
    // Listen for reduced motion preference changes
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    mediaQuery.addEventListener("change", (e) => {
      this.animationFramework.setReducedMotionEnabled(e.matches);
    });

    // Listen for high contrast mode changes
    const contrastQuery = window.matchMedia("(prefers-contrast: high)");
    contrastQuery.addEventListener("change", (e) => {
      document.documentElement.classList.toggle("high-contrast", e.matches);
    });
  }

  /**
   * Set visual feedback state with context
   */
  public async setState(
    state: VisualFeedbackState,
    context: VisualFeedbackContext,
    element?: HTMLElement,
  ): Promise<void> {
    const previousState = this.currentState;
    this.previousState = previousState;
    this.currentState = state;

    // Update context
    this.contextTracker.updateContext(context);

    // Handle state transition
    await this.transitionManager.transitionToState(
      previousState,
      state,
      context,
      element,
    );

    // Apply state-specific behavior
    await this.applyStateSpecificBehavior(state, context, element);
  }

  /**
   * Apply state-specific visual behavior
   */
  private async applyStateSpecificBehavior(
    state: VisualFeedbackState,
    context: VisualFeedbackContext,
    element?: HTMLElement,
  ): Promise<void> {
    switch (state) {
      case VisualFeedbackState.EMPTY:
        await this.applyEmptyState(context, element);
        break;
      case VisualFeedbackState.READY:
        await this.applyReadyState(context, element);
        break;
      case VisualFeedbackState.ACTIVE:
        await this.applyActiveState(context, element);
        break;
      case VisualFeedbackState.SUCCESS:
        await this.applySuccessState(context, element);
        break;
      case VisualFeedbackState.ERROR:
        await this.applyErrorState(context, element);
        break;
    }
  }

  /**
   * Apply Empty/Ready state visual behavior
   */
  private async applyEmptyState(
    context: VisualFeedbackContext,
    element?: HTMLElement,
  ): Promise<void> {
    const config = this.stateConfigurations.getEmptyStateConfig();

    if (element) {
      // Skip styling for canvas elements to prevent canvas-wide highlighting
      if (
        element.hasAttribute("data-canvas-drop-zone") ||
        element.hasAttribute("data-no-visual-feedback")
      ) {
        return;
      }

      // Apply empty state styles
      element.classList.add("terra-canvas-empty");

      // Show help prompt if enabled
      if (config.helpPrompts.enabled) {
        await this.showHelpPrompt(element, config.helpPrompts);
      }

      // Apply grid pattern if enabled
      if (config.gridPattern.enabled) {
        this.applyGridPattern(element, config.gridPattern);
      }

      // Highlight component palette if enabled
      if (config.paletteHighlight.enabled) {
        this.highlightComponentPalette(config.paletteHighlight);
      }
    }
  }

  /**
   * Apply Ready/Hover state visual behavior
   */
  private async applyReadyState(
    context: VisualFeedbackContext,
    element?: HTMLElement,
  ): Promise<void> {
    const config = this.stateConfigurations.getReadyStateConfig();

    if (element) {
      // Skip styling for canvas elements to prevent canvas-wide highlighting
      if (
        element.hasAttribute("data-canvas-drop-zone") ||
        element.hasAttribute("data-no-visual-feedback")
      ) {
        return;
      }

      // Apply drop zone styling
      element.classList.add("terra-drop-zone-ready");

      // Show ghost preview if enabled
      if (config.ghostPreview.enabled) {
        await this.showGhostPreview(element, config.ghostPreview, context);
      }

      // Show alignment guides if enabled
      if (config.alignmentGuides.enabled) {
        this.showAlignmentGuides(element, config.alignmentGuides, context);
      }
    }
  }

  /**
   * Apply Active/Dragging state visual behavior
   */
  private async applyActiveState(
    context: VisualFeedbackContext,
    element?: HTMLElement,
  ): Promise<void> {
    const config = this.stateConfigurations.getActiveStateConfig();

    if (element) {
      // Skip styling for canvas elements to prevent canvas-wide highlighting
      if (
        element.hasAttribute("data-canvas-drop-zone") ||
        element.hasAttribute("data-no-visual-feedback")
      ) {
        return;
      }

      // Apply dragging transformation
      element.classList.add("terra-component-dragging");
      element.classList.add("terra-hardware-accelerated");

      // Create origin marker if enabled
      if (config.originMarker.enabled) {
        this.createOriginMarker(element, config.originMarker);
      }

      // Enable real-time guides if enabled
      if (config.realTimeGuides.enabled) {
        this.enableRealTimeGuides(element, config.realTimeGuides);
      }

      // Enable auto-scroll if enabled
      if (config.autoScroll.enabled) {
        this.enableAutoScroll(element, config.autoScroll);
      }
    }
  }

  /**
   * Apply Success/Placement state visual behavior
   */
  private async applySuccessState(
    context: VisualFeedbackContext,
    element?: HTMLElement,
  ): Promise<void> {
    const config = this.stateConfigurations.getSuccessStateConfig();

    if (element) {
      // Skip styling for canvas elements to prevent canvas-wide highlighting
      if (
        element.hasAttribute("data-canvas-drop-zone") ||
        element.hasAttribute("data-no-visual-feedback")
      ) {
        return;
      }

      // Clean up dragging state
      element.classList.remove("terra-component-dragging");

      // Apply success animation
      element.classList.add("terra-placement-success");

      // Show success indicator if enabled
      if (config.successIndicator.enabled) {
        await this.showSuccessIndicator(element, config.successIndicator);
      }

      // Apply settling effect if enabled
      if (config.settlingEffect.enabled) {
        await this.applySettlingEffect(element, config.settlingEffect);
      }

      // Activate property panel if enabled
      if (config.propertyPanelActivation.enabled) {
        this.activatePropertyPanel(config.propertyPanelActivation);
      }
    }
  }

  /**
   * Apply Error state visual behavior
   */
  private async applyErrorState(
    context: VisualFeedbackContext,
    element?: HTMLElement,
  ): Promise<void> {
    if (element) {
      // Skip styling for canvas elements to prevent canvas-wide highlighting
      if (
        element.hasAttribute("data-canvas-drop-zone") ||
        element.hasAttribute("data-no-visual-feedback")
      ) {
        return;
      }
      element.classList.add("terra-invalid-zone");

      // This will be expanded in the error handling implementation
    }
  }

  // Helper methods for state-specific behaviors
  private async showHelpPrompt(
    element: HTMLElement,
    config: any,
  ): Promise<void> {
    const prompt = document.createElement("div");
    prompt.className = "terra-empty-prompt";
    prompt.textContent = config.text;

    element.appendChild(prompt);

    // Fade in after delay
    setTimeout(() => {
      prompt.style.opacity = "0.6";
    }, config.fadeInDelay);
  }

  private applyGridPattern(element: HTMLElement, config: any): void {
    // Grid pattern is applied via CSS
    element.style.setProperty("--grid-size", `${config.size}px`);
    element.style.setProperty("--grid-opacity", config.opacity.toString());
  }

  private highlightComponentPalette(config: any): void {
    const palette = document.querySelector("[data-component-palette]");
    if (palette) {
      palette.classList.add("terra-palette-highlight");
    }
  }

  private async showGhostPreview(
    element: HTMLElement,
    config: any,
    context: VisualFeedbackContext,
  ): Promise<void> {
    const ghostElement = document.createElement("div");
    ghostElement.className = "terra-ghost-preview";
    ghostElement.style.width = `${context.bounds.width}px`;
    ghostElement.style.height = `${context.bounds.height}px`;
    ghostElement.style.opacity = config.opacity.toString();

    element.appendChild(ghostElement);
  }

  private showAlignmentGuides(
    element: HTMLElement,
    config: any,
    context: VisualFeedbackContext,
  ): void {
    // Create horizontal guide
    const hGuide = document.createElement("div");
    hGuide.className = "terra-snap-guide terra-snap-guide-horizontal";

    // Create vertical guide
    const vGuide = document.createElement("div");
    vGuide.className = "terra-snap-guide terra-snap-guide-vertical";

    element.appendChild(hGuide);
    element.appendChild(vGuide);
  }

  private createOriginMarker(element: HTMLElement, config: any): void {
    const marker = document.createElement("div");
    marker.className = "terra-drag-origin-marker";
    marker.style.width = `${config.size}px`;
    marker.style.height = `${config.size}px`;

    // Position at original location
    const rect = element.getBoundingClientRect();
    marker.style.left = `${rect.left}px`;
    marker.style.top = `${rect.top}px`;

    document.body.appendChild(marker);
  }

  private enableRealTimeGuides(element: HTMLElement, config: any): void {
    // This will be implemented with the full guide system
  }

  private enableAutoScroll(element: HTMLElement, config: any): void {
    // This will be implemented with the auto-scroll system
  }

  private async showSuccessIndicator(
    element: HTMLElement,
    config: any,
  ): Promise<void> {
    const indicator = document.createElement("div");
    indicator.className = "terra-success-indicator";
    indicator.textContent = config.icon;

    element.appendChild(indicator);

    // Remove after display duration
    setTimeout(() => {
      indicator.remove();
    }, config.displayDuration);
  }

  private async applySettlingEffect(
    element: HTMLElement,
    config: any,
  ): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(() => {
        element.style.boxShadow = config.finalShadow;
        element.classList.remove("terra-placement-success");
        resolve();
      }, config.settleSpeed);
    });
  }

  private activatePropertyPanel(config: any): void {
    setTimeout(() => {
      const propertyPanel = document.querySelector("[data-property-panel]");
      if (propertyPanel) {
        propertyPanel.classList.add("active");
      }
    }, config.activationDelay);
  }

  /**
   * Get current state
   */
  public getCurrentState(): VisualFeedbackState {
    return this.currentState;
  }

  /**
   * Get performance metrics
   */
  public getPerformanceMetrics() {
    return this.animationFramework.getPerformanceMetrics();
  }

  /**
   * Enable agricultural context mode for outdoor/gloved operation
   */
  public enableAgriculturalMode(): void {
    document.body.classList.add(
      "terra-agricultural-mode",
      "terra-touch-agricultural",
    );

    // Announce mode change
    this.announceToAccessibilityAPI(
      "Agricultural mode enabled for enhanced outdoor visibility and glove compatibility",
    );

    console.log("Agricultural mode enabled for TerraMatrix Canvas");
  }

  /**
   * Enable outdoor visibility mode
   */
  public enableOutdoorMode(): void {
    document.body.classList.add("terra-outdoor-mode");

    // Announce mode change
    this.announceToAccessibilityAPI(
      "Outdoor visibility mode enabled for enhanced sun visibility",
    );

    console.log("Outdoor visibility mode enabled");
  }

  /**
   * Enable weather resistant mode for extreme conditions
   */
  public enableWeatherResistantMode(): void {
    document.body.classList.add("terra-weather-resistant");

    // Announce mode change
    this.announceToAccessibilityAPI(
      "Weather resistant mode enabled for extreme outdoor conditions",
      "assertive",
    );

    console.log("Weather resistant mode enabled for extreme conditions");
  }

  /**
   * Detect agricultural context and auto-enable optimizations
   */
  public detectAndApplyAgriculturalContext(): void {
    // Detect touch devices (likely tablets in agricultural settings)
    const isTouchDevice =
      "ontouchstart" in window || navigator.maxTouchPoints > 0;

    // Detect outdoor conditions (high brightness, if available)
    const isHighBrightness = this.detectHighBrightnessEnvironment();

    // Auto-enable agricultural mode if conditions suggest outdoor/touch use
    if (isTouchDevice || isHighBrightness) {
      this.enableAgriculturalMode();

      if (isHighBrightness) {
        this.enableOutdoorMode();
      }
    }
  }

  /**
   * Detect high brightness environment (simplified heuristic)
   */
  private detectHighBrightnessEnvironment(): boolean {
    // Simple heuristic: check if user prefers high contrast (might indicate outdoor use)
    if (
      window.matchMedia &&
      window.matchMedia("(prefers-contrast: high)").matches
    ) {
      return true;
    }

    // Check for reduced transparency preference (another outdoor indicator)
    if (
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-transparency: reduce)").matches
    ) {
      return true;
    }

    return false;
  }

  /**
   * Announce to accessibility API (screen readers)
   */
  private announceToAccessibilityAPI(
    message: string,
    priority: "polite" | "assertive" = "polite",
  ): void {
    // Create or use existing live region
    let liveRegion = document.getElementById(
      "terra-agricultural-announcements",
    );
    if (!liveRegion) {
      liveRegion = document.createElement("div");
      liveRegion.id = "terra-agricultural-announcements";
      liveRegion.setAttribute("aria-live", priority);
      liveRegion.setAttribute("aria-atomic", "true");
      liveRegion.style.cssText = `
        position: absolute;
        left: -10000px;
        width: 1px;
        height: 1px;
        overflow: hidden;
      `;
      document.body.appendChild(liveRegion);
    }

    // Update the live region
    liveRegion.textContent = message;

    // Clear after announcement
    setTimeout(() => {
      liveRegion!.textContent = "";
    }, 1000);
  }

  /**
   * Clean up resources
   */
  public dispose(): void {
    this.animationFramework.dispose();
    this.activeElements.clear();

    // Remove stylesheet
    const styleSheet = document.getElementById("terra-visual-feedback-styles");
    if (styleSheet) {
      styleSheet.remove();
    }
  }
}

/**
 * Supporting classes
 */
class StateConfigurationManager {
  private emptyConfig: EmptyStateConfig;
  private readyConfig: ReadyStateConfig;
  private activeConfig: ActiveStateConfig;
  private successConfig: SuccessStateConfig;

  setEmptyStateConfig(config: EmptyStateConfig) {
    this.emptyConfig = config;
  }

  setReadyStateConfig(config: ReadyStateConfig) {
    this.readyConfig = config;
  }

  setActiveStateConfig(config: ActiveStateConfig) {
    this.activeConfig = config;
  }

  setSuccessStateConfig(config: SuccessStateConfig) {
    this.successConfig = config;
  }

  getEmptyStateConfig(): EmptyStateConfig {
    return this.emptyConfig;
  }

  getReadyStateConfig(): ReadyStateConfig {
    return this.readyConfig;
  }

  getActiveStateConfig(): ActiveStateConfig {
    return this.activeConfig;
  }

  getSuccessStateConfig(): SuccessStateConfig {
    return this.successConfig;
  }
}

class StateTransitionManager {
  constructor(
    private animationFramework: HardwareAcceleratedAnimationFramework,
  ) {}

  async transitionToState(
    from: VisualFeedbackState,
    to: VisualFeedbackState,
    context: VisualFeedbackContext,
    element?: HTMLElement,
  ): Promise<void> {
    // Implement state transition logic
    if (element && from !== to) {
      // Clean up previous state
      this.cleanupPreviousState(from, element);

      // Apply transition animation
      await this.applyTransitionAnimation(from, to, element);
    }
  }

  private cleanupPreviousState(
    state: VisualFeedbackState,
    element: HTMLElement,
  ): void {
    const classesToRemove = [
      "terra-canvas-empty",
      "terra-drop-zone-ready",
      "terra-component-dragging",
      "terra-placement-success",
      "terra-invalid-zone",
    ];

    classesToRemove.forEach((cls) => element.classList.remove(cls));

    // Remove state-specific elements
    element
      .querySelectorAll(
        ".terra-empty-prompt, .terra-ghost-preview, .terra-snap-guide, .terra-success-indicator",
      )
      .forEach((el) => el.remove());
  }

  private async applyTransitionAnimation(
    from: VisualFeedbackState,
    to: VisualFeedbackState,
    element: HTMLElement,
  ): Promise<void> {
    // Apply smooth transitions between states
    const transitionDuration = 200;

    await this.animationFramework.createAnimation(
      element,
      {
        transform: "translateZ(0)",
        opacity: 1,
        willChange: ["opacity"],
        backfaceVisibility: "hidden",
        perspective: 1000,
      },
      {
        duration: transitionDuration,
        easing: "ease-out",
        delay: 0,
        iterations: 1,
        direction: "normal",
        fillMode: "forwards",
      },
    );
  }
}

class ContextTracker {
  private currentContext: VisualFeedbackContext | null = null;

  updateContext(context: VisualFeedbackContext): void {
    this.currentContext = context;
  }

  getCurrentContext(): VisualFeedbackContext | null {
    return this.currentContext;
  }
}
